import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { FunctionComponent, useState } from 'react';
import { sendEmailVerification } from '../../api/AuthApi';
import arrowRight from '../../assets/svgs/arrow-right.svg';
import emailVerificationLogo from '../../assets/svgs/email-verification.svg';
import connectMindsetLogo from '../../assets/svgs/logos/connect-mindset-logo.svg';
import { AlertBar } from '../../components/AlertBar';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { useAuthStore } from '../../store/AuthStore';
import { colors } from '../../theme/colors';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    paddingBottom: '20px',
    paddingTop: `${theme.spacing(3)}px`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `${theme.spacing(8)}px`,
    },
  },
  cardRoot: {
    borderRadius: 23,
    backgroundColor: colors.xLightGray,
    marginTop: 55,
    position: 'relative',
    overflow: 'inherit',
  },
  cardContentRoot: {
    paddingTop: 30,
  },
  listRoot: {
    margin: 'auto',
    width: 'fit-content',
  },
  listItemPadding: {
    paddingBottom: '0px',
  },
}));

export const EmailVerificationScreen: FunctionComponent = () => {
  const classes = useStyles();
  const userEmail = useAuthStore((state) => state.firebaseUser?.email);
  const [isLoading, setIsLoading] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openFailureAlert, setOpenFailureAlert] = useState(false);

  async function resendEmailVerification() {
    try {
      setIsLoading(true);
      await sendEmailVerification();
      Analytics.track({
        event: AnalyticsEventName.a_verification_email_resent,
      });
      setIsLoading(false);
      setOpenSuccessAlert(true);
    } catch (error) {
      setIsLoading(false);
      setOpenFailureAlert(true);
    }
  }

  return (
    <Container maxWidth="sm" data-testid="email-verification-screen">
      <AlertBar
        copy="Something went wrong!"
        status="error"
        open={openFailureAlert}
        onClose={setOpenFailureAlert}
      />
      <AlertBar
        copy="Email resent!"
        status="success"
        open={openSuccessAlert}
        onClose={setOpenSuccessAlert}
      />

      <Grid container direction="column" alignItems="center" classes={{ root: classes.root }}>
        <Grid container item direction="column" alignItems="center">
          <Box marginBottom="40px">
            <img src={connectMindsetLogo} alt="mindset health logo" width={220} height={93} />
          </Box>
        </Grid>
        <Box textAlign="center">
          <Typography variant="h4">Please verify your email</Typography>
        </Box>
        <Box display="flex" flexDirection="column" mt="20px" textAlign="center">
          <Typography>An email has been sent to</Typography>
          <Typography style={{ fontWeight: 700 }}>{userEmail}</Typography>
        </Box>
        <Box>
          <Card elevation={0} classes={{ root: classes.cardRoot }}>
            <Box
              position="absolute"
              top="-30px"
              right={0}
              left={0}
              marginLeft="auto"
              marginRight="auto"
              width={58}
            >
              <img src={emailVerificationLogo} alt="email verification" width={58} height={58} />
            </Box>
            <CardContent classes={{ root: classes.cardContentRoot }}>
              <Box textAlign="center" maxWidth={365} marginLeft="auto" marginRight="auto">
                <Typography>
                  Privacy is very important to us. Please follow the instructions in the
                  verification email to finish creating your account.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flexDirection="column" mt="30px" textAlign="center">
          <Typography style={{ fontWeight: 700 }} paragraph={true}>
            You now have free access to Nerva, Evia, Finito, Claria, and Relio.
          </Typography>
          <Typography paragraph={true}>
            Simply download the app and log in with the email and password you just entered to get
            started
          </Typography>
          <Typography style={{ fontWeight: 700 }}>
            After verifying, you will receive via email:
          </Typography>
          <List classes={{ root: classes.listRoot }}>
            <Box display="flex" alignItems="baseline">
              <Box
                height="5px"
                width="5px"
                bgcolor={colors.black}
                borderRadius="50%"
                display="inline-block"
              />
              <ListItem classes={{ root: classes.listItemPadding }}>
                An invitation to a personalized onboarding call
              </ListItem>
            </Box>
            <Box display="flex" alignItems="baseline">
              <Box
                height="5px"
                width="5px"
                bgcolor={colors.black}
                borderRadius="50%"
                display="inline-block"
              />
              <ListItem classes={{ root: classes.listItemPadding }}>
                Clinical resources (e.g white papers &amp; patient overviews)
              </ListItem>
            </Box>
            <Box display="flex" alignItems="baseline">
              <Box
                height="5px"
                width="5px"
                bgcolor={colors.black}
                borderRadius="50%"
                display="inline-block"
              />
              <ListItem classes={{ root: classes.listItemPadding }}>
                Information on how to integrate Mindset Health programs into your practice
              </ListItem>
            </Box>
          </List>
        </Box>

        <Box marginTop="20px" textAlign="center" width={270} marginLeft="auto" marginRight="auto">
          <Box marginBottom={2}>
            <Typography>Didn&apos;t receive the email?</Typography>
          </Box>

          <Button
            onClick={resendEmailVerification}
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            disabled={isLoading}
            endIcon={
              <img src={arrowRight} width={20} height={20} alt="resend email verification arrow" />
            }
          >
            Resend verification email
            <Box position="absolute" bottom="0">
              {isLoading && <CircularProgress color="primary" size={24} />}
            </Box>
          </Button>
        </Box>
      </Grid>
    </Container>
  );
};
